import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Clock, Calendar, User } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import CTACard from "@/components/blog/CTACard";
import BlogImage from "@/components/blog/BlogImage";
import ScrollToTopButton from "@/components/ScrollToTopButton";
import { getPostBySlug } from "@/data/blogPosts";
const TecnicasProcrastinar = () => {
  const post = getPostBySlug("5-tecnicas-para-nunca-mais-procrastinar");

  if (!post) return null;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{post.metaTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <meta property="og:title" content={post.metaTitle} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.metaTitle} />
        <meta name="twitter:description" content={post.metaDescription} />
        <link rel="canonical" href={`https://habitfrog.com/posts/${post.slug}`} />
      </Helmet>

      <Header />
      <ScrollToTopButton />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          {/* Breadcrumb */}
          <nav className="mb-8" aria-label="Breadcrumb">
            <Link to="/home" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Voltar ao início
            </Link>
          </nav>

          {/* Article Header */}
          <header className="max-w-4xl mx-auto mb-12">
            <span className="inline-block bg-coral/20 text-coral px-3 py-1 rounded-full text-sm font-semibold mb-4">
              {post.category}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground mb-6 leading-tight">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-muted-foreground">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{post.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{post.readTime}</span>
              </div>
            </div>
          </header>

          {/* Article Content */}
          <article className="max-w-4xl mx-auto prose prose-lg max-w-none">
            {/* Introdução */}
            <div className="mb-12">
              <p className="text-xl text-muted-foreground leading-relaxed">
                Você costuma deixar tarefas importantes para depois, correndo contra prazos no último minuto? Se sente culpado, ansioso e sobrecarregado por adiar aquilo que sabe que precisa ser feito? Saiba que você não está sozinho. A procrastinação é um hábito comum que já atrapalhou a produtividade e os sonhos de muita gente, destruindo planos antes mesmo de saírem do papel. Mas a boa notícia é que é possível parar de procrastinar e retomar o controle da sua rotina.
              </p>
            </div>

            {/* Grid de duas colunas */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div>
                <p className="text-foreground/80">
                  Procrastinar significa, em termos simples, adiar ou postergar tarefas que precisam ser realizadas. Embora pareça inofensivo "deixar para amanhã", esse comportamento pode ter consequências sérias na sua vida profissional, acadêmica e pessoal. O acúmulo de tarefas gera estresse, ansiedade e aquela constante sensação de estar atrasado.
                </p>
                <p className="text-foreground/80">
                  Todos nós estamos sujeitos a cair nessa armadilha – seja por tarefas muito difíceis, medo de falhar, falta de foco ou até perfeccionismo. Identificar por que você procrastina (tédio, insegurança, cansaço?) é um passo importante para enfrentar o problema de frente.
                </p>
              </div>
              <div>
                <p className="text-foreground/80">
                  A procrastinação não é sinônimo de preguiça. Muitas vezes, adiamos algo importante porque nos sentimos sobrecarregados ou não sabemos por onde começar. Felizmente, com as estratégias certas, é possível vencer a procrastinação e desenvolver hábitos mais produtivos.
                </p>
                <p className="text-foreground/80">
                  Quando você entende as causas do adiamento e aplica técnicas específicas no dia a dia, aumenta sua disciplina, reduz a ansiedade e melhora sua eficiência em tudo que faz. Ou seja, dá para quebrar o ciclo vicioso da postergação e substituí-lo por um ciclo virtuoso de realização.
                </p>
              </div>
            </div>

            <BlogImage 
              src="/images/blog/pessoa-cansada-trabalho.jpg" 
              alt="Pessoa exausta no trabalho olhando para tela do computador, representando o esgotamento causado pela procrastinação e adiamento de tarefas" 
              className="h-64 md:h-80 mb-12"
              priority
            />

            <p className="text-lg text-foreground/90 mb-12 bg-accent/10 p-6 rounded-xl border-l-4 border-primary">
              <strong>Pronto para mudar?</strong> Então confira a seguir 5 técnicas infalíveis de como parar de procrastinar. São dicas práticas, testadas e aprovadas, que vão ajudar você a criar hábitos positivos, aumentar sua produtividade, manter o foco e deixar a procrastinação para trás de uma vez por todas. Vamos lá!
            </p>

            {/* Técnica 1 */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold">1</span>
                Divida grandes tarefas em etapas menores
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-card p-6 rounded-xl border">
                  <h3 className="font-semibold text-foreground mb-3">O Problema</h3>
                  <p className="text-sm text-muted-foreground">Tarefas muito grandes ou complexas podem causar paralisação. Quando olhamos para um projeto inteiro – aquele TCC de dezenas de páginas ou a arrumação completa da casa – bate a sensação de sobrecarga.</p>
                </div>
                <div className="bg-card p-6 rounded-xl border">
                  <h3 className="font-semibold text-foreground mb-3">A Solução</h3>
                  <p className="text-sm text-muted-foreground">Quebrar a tarefa em partes menores e manejáveis. Em vez de encarar tudo de uma vez, defina microetapas: "escrever a introdução" ou "pesquisar dados do capítulo 1".</p>
                </div>
                <div className="bg-card p-6 rounded-xl border">
                  <h3 className="font-semibold text-foreground mb-3">O Benefício</h3>
                  <p className="text-sm text-muted-foreground">Cada pequena conclusão dá uma sensação de progresso. Pequenas vitórias liberam dopamina – o neurotransmissor do prazer e motivação.</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
                <div>
                  <p className="text-foreground/80 mb-4">
                    Ao concluir uma etapa menor, nosso cérebro recebe uma recompensa imediata por meio da sensação de realização. Você se sente motivado a partir para a próxima parte, numa sequência positiva. Pequenas vitórias liberam dopamina – o neurotransmissor do prazer e motivação – fazendo com que avançar para o próximo passo se torne mais gostoso.
                  </p>
                  <p className="text-foreground/80 mb-4">
                    Dessa forma, dividir o trabalho em pedaços não só torna a tarefa menos intimidante, como também aumenta sua motivação conforme você vai ticando cada sub-tarefa concluída.
                  </p>
                  <div className="bg-primary/10 p-4 rounded-lg border border-primary/30">
                    <p className="text-sm font-medium text-foreground">
                      💡 <strong>Regra dos 5 minutos</strong> – Diga a si mesmo que vai trabalhar naquela tarefa por apenas cinco minutos. Pegue aquele primeiro pedacinho e comece a fazê-lo sem compromisso de ir além. Muitas vezes, dar o primeiro passo quebra a inércia da procrastinação.
                    </p>
                  </div>
                </div>
                <BlogImage 
                  src="/images/blog/checklist-tarefas.jpg" 
                  alt="Checklist visual com tarefas sendo marcadas como concluídas, representando o progresso por etapas" 
                  className="h-64"
                />
              </div>

              <p className="text-foreground/80">
                Passados os cinco minutos iniciais, você provavelmente terá engajado na atividade e continuará naturalmente. E mesmo que pare após esse tempo, pelo menos você já saiu da estagnação – o que é muito melhor do que não ter feito nada. <strong>Lembre-se: começar é metade do caminho!</strong>
              </p>
            </section>

            {/* Técnica 2 */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold">2</span>
                Use técnicas de gestão do tempo (ex.: Pomodoro)
              </h2>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <p className="text-foreground/80 mb-4">
                    Outra técnica poderosa para vencer a procrastinação é gerenciar melhor o seu tempo durante as atividades. Uma das ferramentas mais consagradas é a <strong>Técnica Pomodoro</strong>. Ela consiste em dividir seu trabalho em blocos focados de aproximadamente 25 minutos, seguidos de breves intervalos de 5 minutos.
                  </p>
                  <p className="text-foreground/80 mb-4">
                    A ideia é simples: você se compromete a focar intensamente na tarefa por aquele curto período (chamado de "pomodoro"), sem interrupções, e então se recompensa com uma pausa rápida. Após quatro ciclos, faz uma pausa mais longa de 15–30 minutos para recarregar as energias.
                  </p>
                  <p className="text-foreground/80">
                    Saber que terá uma pausa em poucos minutos ajuda a manter a mente no presente, fazendo com que você produza mais em menos tempo do que tentando passar horas seguidas em atenção difusa.
                  </p>
                </div>
                <BlogImage 
                  src="/images/blog/pomodoro-timer.jpg" 
                  alt="Timer de Pomodoro mostrando 25 minutos com interface visual atraente para técnica de gestão do tempo" 
                  className="h-64"
                />
              </div>

              <div className="bg-gradient-to-r from-card to-accent/10 p-8 rounded-2xl border mb-8">
                <h3 className="text-xl font-bold text-foreground mb-4">Como funciona o Pomodoro:</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto bg-primary/20 rounded-full flex items-center justify-center mb-2">
                      <span className="text-2xl font-bold text-primary">25</span>
                    </div>
                    <p className="text-sm text-muted-foreground">min de foco</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto bg-accent/30 rounded-full flex items-center justify-center mb-2">
                      <span className="text-2xl font-bold">5</span>
                    </div>
                    <p className="text-sm text-muted-foreground">min de pausa</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto bg-secondary/50 rounded-full flex items-center justify-center mb-2">
                      <span className="text-2xl font-bold">4x</span>
                    </div>
                    <p className="text-sm text-muted-foreground">ciclos</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto bg-primary/30 rounded-full flex items-center justify-center mb-2">
                      <span className="text-2xl font-bold text-primary">30</span>
                    </div>
                    <p className="text-sm text-muted-foreground">min pausa longa</p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div className="bg-card p-6 rounded-xl border">
                  <h3 className="font-semibold text-foreground mb-3">🚫 Elimine Distrações</h3>
                  <p className="text-sm text-muted-foreground">
                    Antes de começar, feche redes sociais, silencie ou afaste o celular e desligue notificações desnecessárias. Se possível, deixe à mão somente o que for usar na tarefa. Crie um ambiente propício para se concentrar apenas na atividade proposta.
                  </p>
                </div>
                <div className="bg-card p-6 rounded-xl border">
                  <h3 className="font-semibold text-foreground mb-3">☕ Descanse de Verdade</h3>
                  <p className="text-sm text-muted-foreground">
                    Nas pausas, levante da cadeira, alongue o corpo, tome uma água ou café, olhe pela janela. Evite engatar em outra atividade que exija esforço mental durante seus 5 minutos de intervalo.
                  </p>
                </div>
              </div>

              <p className="text-foreground/80">
                Ao respeitar os intervalos e não se sobrecarregar, você retorna revigorado para mais um pomodoro. Com o tempo, esse ritmo de foco alternado com descanso se torna natural e você perceberá um aumento significativo no seu poder de concentração.
              </p>
            </section>

            <div className="my-16"><CTACard variant="primary" /></div>

            {/* Técnica 3 */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold">3</span>
                Estabeleça metas claras e prazos realistas
              </h2>

              <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
                <BlogImage 
                  src="/images/blog/calendario-metas.jpg" 
                  alt="Calendário com metas e prazos marcados, mostrando organização visual de objetivos para combater procrastinação" 
                  className="h-64"
                />
                <div>
                  <p className="text-foreground/80 mb-4">
                    Se não houver prazo ou meta definida, a tendência é empurrar a tarefa indefinidamente. Por isso, crie compromissos no calendário para aquilo que precisa ser feito. Definir prazos concretos gera um senso de urgência saudável e ajuda a combater a procrastinação.
                  </p>
                  <p className="text-foreground/80 mb-4">
                    Coloque datas-limite mesmo em tarefas que originalmente não as tenham – por exemplo, se você quer ler um livro, determine um dia para terminar cada capítulo.
                  </p>
                  <p className="text-foreground/80">
                    <strong>Prazo definido transforma um desejo vago em um objetivo com data marcada, diminuindo a chance de você deixar "para algum dia".</strong>
                  </p>
                </div>
              </div>

              <div className="bg-accent/10 p-6 rounded-xl border-l-4 border-coral mb-8">
                <h3 className="font-bold text-foreground mb-2">⚠️ Atenção: Metas Realistas</h3>
                <p className="text-foreground/80">
                  Tenha cuidado em estabelecer metas realistas e proporcionais à sua rotina. Objetivos ou prazos irreais acabam tendo efeito contrário: ao não conseguir cumpri-los, você se sentirá frustrado e desmotivado, alimentando ainda mais a procrastinação.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-card p-6 rounded-xl border text-center">
                  <div className="text-4xl mb-4">📅</div>
                  <h3 className="font-semibold text-foreground mb-2">Prazos Intermediários</h3>
                  <p className="text-sm text-muted-foreground">Distribua marcos ao longo do caminho para projetos longos.</p>
                </div>
                <div className="bg-card p-6 rounded-xl border text-center">
                  <div className="text-4xl mb-4">✅</div>
                  <h3 className="font-semibold text-foreground mb-2">Checkpoints</h3>
                  <p className="text-sm text-muted-foreground">Esses prazos menores funcionam como checkpoints que mantêm você no ritmo certo.</p>
                </div>
                <div className="bg-card p-6 rounded-xl border text-center">
                  <div className="text-4xl mb-4">🎯</div>
                  <h3 className="font-semibold text-foreground mb-2">Metas Alcançáveis</h3>
                  <p className="text-sm text-muted-foreground">Estabeleça objetivos que você consiga cumprir com dedicação.</p>
                </div>
              </div>
            </section>

            {/* Técnica 4 */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold">4</span>
                Priorize tarefas importantes para manter o foco
              </h2>

              <p className="text-foreground/80 mb-8">
                Uma das razões de procrastinarmos é não ter clareza do que precisa ser feito ou por onde começar. Aqui entra a importância de organizar suas tarefas diárias.
              </p>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div className="bg-card p-6 rounded-xl border">
                  <h3 className="font-semibold text-foreground mb-4">📝 Crie uma Lista de Afazeres</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Comece criando uma lista de afazeres (to-do list) todas as manhãs ou na noite anterior. Coloque no papel tudo o que você precisa realizar.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <strong>Riscar itens da lista traz uma sensação deliciosa de progresso e produtividade!</strong>
                  </p>
                </div>
                <div className="bg-card p-6 rounded-xl border">
                  <h3 className="font-semibold text-foreground mb-4">🔝 Defina Prioridades</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Identifique quais são as tarefas mais críticas – aquelas que têm maior impacto nos seus objetivos ou prazos mais apertados.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Comece o dia executando as tarefas mais importantes e desafiadoras primeiro.
                  </p>
                </div>
              </div>

              <BlogImage 
                src="/images/blog/foco-trabalho.jpg" 
                alt="Mesa de trabalho organizada com lista de tarefas priorizadas, café e ambiente de foco para produtividade" 
                className="h-64 md:h-80 mb-8"
              />

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-4">📆 Crie uma Rotina Estruturada</h3>
                  <p className="text-foreground/80 mb-4">
                    Quando você estabelece horários fixos para certas atividades e segue um ritual diário, seu cérebro entra no "modo automático" positivo.
                  </p>
                  <p className="text-foreground/80">
                    Manter uma rotina consistente comunica à sua mente que "é hora de trabalhar" ou "é hora de descansar", diminuindo a chance de procrastinação.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-4">📱 Use a Tecnologia a seu Favor</h3>
                  <p className="text-foreground/80 mb-4">
                    Há diversos recursos que podem ajudar na organização: agendas, calendários, planners e aplicativos de produtividade.
                  </p>
                  <p className="text-foreground/80">
                    Essas ferramentas são ótimas para você acompanhar sua evolução e identificar padrões.
                  </p>
                </div>
              </div>
            </section>

            {/* Técnica 5 */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold">5</span>
                Mantenha a motivação: recompensas, atitude positiva e apoio
              </h2>

              <p className="text-foreground/80 mb-8">
                Motivar-se é essencial para parar de procrastinar de vez, e existem algumas estratégias simples para isso.
              </p>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-card p-6 rounded-xl border">
                  <div className="text-4xl mb-4">🎁</div>
                  <h3 className="font-semibold text-foreground mb-3">Recompensas</h3>
                  <p className="text-sm text-muted-foreground">
                    A cada tarefa concluída ou meta atingida, conceda-se uma pequena recompensa: um lanche, um episódio de série, uma volta no quarteirão.
                  </p>
                </div>
                <div className="bg-card p-6 rounded-xl border">
                  <div className="text-4xl mb-4">💪</div>
                  <h3 className="font-semibold text-foreground mb-3">Atitude Positiva</h3>
                  <p className="text-sm text-muted-foreground">
                    Evite se repreender excessivamente por ter procrastinado. Pratique a autocompaixão e enfoque no que pode melhorar daqui em diante.
                  </p>
                </div>
                <div className="bg-card p-6 rounded-xl border">
                  <div className="text-4xl mb-4">👥</div>
                  <h3 className="font-semibold text-foreground mb-3">Apoio e Accountability</h3>
                  <p className="text-sm text-muted-foreground">
                    Divida seus objetivos com alguém de confiança e peça para essa pessoa lhe cobrar ou acompanhar seu progresso.
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-4">🎮 Torne o Processo Divertido</h3>
                  <p className="text-foreground/80 mb-4">
                    A gente tende a procrastinar menos quando sente prazer ou desafio na atividade. Que tal encarar suas tarefas como parte de um jogo? Você pode <strong>gamificar sua rotina</strong>, transformando metas em missões a cumprir.
                  </p>
                  <p className="text-foreground/80">
                    Ao encarar as obrigações como um jogo, você muda a perspectiva: tarefas antes chatas ganham um elemento de diversão.
                  </p>
                </div>
                <BlogImage 
                  src="/images/blog/checklist-tarefas.jpg" 
                  alt="Aplicativo gamificado de hábitos com níveis, conquistas, pontos e progresso visual para combater procrastinação" 
                  className="h-64"
                />
              </div>

              <div className="bg-gradient-to-r from-primary/10 to-accent/10 p-8 rounded-2xl border mb-8">
                <h3 className="text-xl font-bold text-foreground mb-4">🏆 Celebre e Compartilhe suas Conquistas</h3>
                <p className="text-foreground/80 mb-4">
                  Sempre que possível, reconheça o seu próprio progresso e valorize as etapas vencidas. Participe de grupos ou comunidades com objetivos semelhantes aos seus.
                </p>
                <p className="text-foreground/80">
                  <strong>Lembre-se de que motivação é algo que precisa ser alimentado continuamente.</strong>
                </p>
              </div>
            </section>

            {/* Conclusão */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6">Conclusão: transforme seus hábitos e dê o próximo passo</h2>
              
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <p className="text-foreground/80 mb-4">
                    Acabar com a procrastinação não acontece do dia para a noite, mas seguindo essas técnicas diariamente você vai notar uma grande transformação nos seus hábitos.
                  </p>
                  <p className="text-foreground/80 mb-4">
                    Imagine como será cumprir suas metas no prazo, ver seu checklist do dia todo concluído e poder aproveitar seu tempo livre sem aquela sombra de tarefas atrasadas.
                  </p>
                  <p className="text-foreground/80">
                    Tenha paciência consigo mesmo durante o processo e comemore cada melhoria, por menor que seja.
                  </p>
                </div>
                <BlogImage 
                  src="/images/blog/equipe-colaborativa.jpg" 
                  alt="Pessoa celebrando ao completar todas as tarefas do dia com expressão de satisfação e produtividade" 
                  className="h-64"
                />
              </div>

              <p className="text-xl font-semibold text-primary text-center py-4">
                Você já aprendeu como parar de procrastinar, agora só falta colocar em prática. O melhor momento para agir é já! Embarque nessa jornada de evolução e conquiste tudo aquilo que você merece, um hábito de cada vez.
              </p>
            </section>

            <CTACard variant="secondary" />
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TecnicasProcrastinar;
